import { useState, useEffect, useMemo } from 'react';
import OrgLink, { orgCrumb } from '../components/OrgLink';
import { useInternalOrgId } from '../hooks/useInternalOrg';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ScreenLink as Link } from '../components/permissions/ScreenLink';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';
import {
  Plus, Trash2, Flag, Pencil, FolderKanban, ListTodo, Clock, MoreHorizontal,
  Layers, Shield, SearchX, Ban, Archive, ArchiveRestore, Wallet, FileStack, ListChecks, Milestone, RotateCcw,
} from 'lucide-react';

// Charts load on demand (recharts stays out of the first bundle).
import DeadlineBanner from '../components/DeadlineBanner';
import CharacterizationBanner from '../components/CustomerHub/CharacterizationBanner';
import StatusBadge from '../components/StatusBadge';
import EmptyState from '../components/EmptyState';
import DataTable, { type CardSpec, type Column } from '../components/DataTable';
import MenuButton from '../components/table/MenuButton';
import { NewWorkLogModal } from '../components/NewWorkLogModal';
import Badge from '../components/ui/Badge';
import StatusPillSelect from '../components/common/StatusPillSelect';
import { CHANGEABLE_STATUSES, needsWarrantyOnStatus, warrantyOf, type WarrantyPatch } from '../components/projects/projectModel';
import Tabs from '../components/ui/Tabs';
import Dialog from '../components/ui/Dialog';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import { useConfirmLock } from '../hooks/useConfirmLock';
import Field from '../components/ui/Field';
import FilterBar, { type FilterDef } from '../components/ui/FilterBar';
import StateBlock from '../components/ui/StateBlock';
import type { MenuItemDef } from '../components/ui/Menu';
import { statusTone } from '../components/ui/tone';
import DetailHeader from '../components/details/DetailHeader';
import CancellationNote from '../components/details/CancellationNote';
import DetailSection from '../components/details/DetailSection';
import DetailSkeleton from '../components/details/DetailSkeleton';
import MilestoneProgressList from '../components/details/MilestoneProgressList';
import TaskFormModal from '../components/projects/TaskFormModal';
import { usePermissions } from '../hooks/usePermissions';
import MilestoneFormModal from '../components/projects/MilestoneFormModal';
import ProjectMilestonesTab from '../components/projects/ProjectMilestonesTab';
import BulkAddModal from '../components/projects/BulkAddModal';
import { ServerError, SubmitButton } from '../components/projects/formBits';
import { getProjectStatusLabel, getProjectTypeLabel, type Ticket } from '../types';
import { formatDate, formatNumber } from '../utils/format';
import Kpi from '../components/ui/Kpi';
import { toast } from '../store/toastStore';
import { apiErrorMessage } from '../components/sales/shared';
import { useTaskRowActions } from '../components/tasks/useTaskRowActions';
import { taskListColumns, DueWithLate } from '../components/tasks/taskColumns';
import { assigneeNames, taskPath } from '../components/tasks/taskRowModel';
import { milestoneTree } from '../components/engagements/EngagementChainFields';
import { projectTaskGroups } from '../components/projects/milestoneListModel';
import { PageChrome } from '../hooks/usePageChrome';
import TaskHasWorkDialog, { taskHasWorkMinutes } from '../components/tasks/TaskHasWorkDialog';
import ProjectFormModal from '../components/projects/ProjectFormModal';
import ProjectHoursTab from '../components/projects/ProjectHoursTab';
import ProjectTeamTab from '../components/projects/ProjectTeamTab';
import ProjectProfitabilityTab, { NegativeProfitNotice } from '../components/profitability/ProjectProfitabilityTab';
import { showProfitTab } from '../components/profitability/profitabilityModel';
import {
  ProjectDocumentsDialog, ProjectFinancialsDialog, ProjectPhaseDialog, ProjectStatusDialog,
} from '../components/projects/ProjectDialogs';
import { HEALTH_TONE, hoursText } from '../components/projects/projectModel';
import DirIcon from '../components/ui/DirIcon';
import { useIsDesktop } from '../components/ui/useOverlay';
import type { CorePhase } from '../types';
import { undoableDeleteProject, undoableDeleteTicket, DELETE_KINDS } from '../utils/undoableDeletes';
import { offerUndoStatus, statusIsReversible, useWithoutHidden } from '../utils/undoableActions';
import ProjectCharacterizationDocs from '../components/documentsReview/ProjectCharacterizationDocs';
/** P-27: statuses whose transition has one-way effects (completion events, cancel reason). */
const PROJECT_ONE_WAY_STATUSES = ['completed', 'cancelled'] as const;

// ============================================================
// Main ProjectPage
// ============================================================
type ProjectTab = 'milestones' | 'tasks' | 'hours' | 'team' | 'profitability';
/** P-04/C (D-39) — the tabs, in this order. */
const PROJECT_TABS: ProjectTab[] = ['milestones', 'tasks', 'hours', 'team', 'profitability'];

/**
 * Project page (P-04/C, D-39). Header: breadcrumb customer › project ("חזרה"
 * is the TopBar's, on every detail page — back in the browser history); the
 * name with "status — phase" ("פעיל — QA"); [✎ עריכה] [+ אבן דרך] [+ משימה]
 * [⋯]. Data lines: customer · project manager · team lead; dates (+ lateness)
 * · hours planned / actual · health; progress. Under them: [הוספת אבני דרך
 * ומשימות] [פרטים כספיים] [מסמכים] — each a dialog. Tabs: אבני דרך | משימות |
 * שעות | צוות, in `?tab=` so back / forward restore them.
 */
export default function ProjectPage() {
  const internalOrgId = useInternalOrgId();
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useAuthStore((s) => s.user);
  const [showCreateMilestone, setShowCreateMilestone] = useState(false);
  // P-56 (D-231 §4) — "+ תת-אבן דרך" from a row of the milestones tab: the main milestone.
  const [subParentId, setSubParentId] = useState<string | null>(null);
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [editingTask, setEditingTask] = useState<any>(null);
  const [editingMilestone, setEditingMilestone] = useState<any>(null);
  // P-05/C — "שמירה והוספת משימות לאבן דרך זו": the task form locked on that milestone.
  const [addTasksTo, setAddTasksTo] = useState<{ id: string } | null>(null);
  const [showDeleteProject, setShowDeleteProject] = useState(false);
  const [showBulkAdd, setShowBulkAdd] = useState(false);
  const [showSla, setShowSla] = useState(false);
  const [showCancelProject, setShowCancelProject] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showFinancials, setShowFinancials] = useState(false);
  const [showDocuments, setShowDocuments] = useState(false);
  const [showStatus, setShowStatus] = useState(false);
  const [pickedStatus, setPickedStatus] = useState<string | undefined>(undefined);
  const [showPhase, setShowPhase] = useState(false);

  const tabParam = searchParams.get('tab') as ProjectTab | null;
  const requestedTab: ProjectTab = tabParam && PROJECT_TABS.includes(tabParam) ? tabParam : 'milestones';
  // A push (not a replace), so the browser's back / forward walk through the tabs.
  const setActiveTab = (tab: ProjectTab) => setSearchParams((prev) => {
    const next = new URLSearchParams(prev);
    if (tab === 'milestones') next.delete('tab'); else next.set('tab', tab);
    return next;
  });

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: projectData, isLoading, error: projectError, refetch, isFetching } = useQuery({
    queryKey: ['project', id],
    queryFn: () => api.getProject(id!),
    enabled: !!id,
  });

  const project = projectData?.data;
  // P-28b (D-169): "רווחיות" is an extra tab, for who sees money only (D-15, D-95).
  const canProfit = showProfitTab(project?.permissions?.can_view_financials);
  const activeTab: ProjectTab = requestedTab === 'profitability' && !canProfit ? 'milestones' : requestedTab;
  const isAdmin = user?.role === 'admin';

  // QA-R2/15 (D-33) — "עריכת הפרויקט" from the missing-financials notice opens
  // the project's edit form (`?edit=1`), for who may edit it.
  const canEditFromLink = project?.permissions?.can_edit === true;
  useEffect(() => {
    if (searchParams.get('edit') !== '1' || !project) return;
    setShowCreateTask(false);
    setAddTasksTo(null);
    setShowBulkAdd(false);
    if (canEditFromLink) setShowEdit(true);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('edit');
      return next;
    }, { replace: true });
  }, [searchParams, project, canEditFromLink, setSearchParams]);

  // Delete project mutation
  // P-27 (D-166 s.11): the delete leaves the server after 6 s; "בטל" cancels it and
  // brings the project page back (a refusal does too, with the same error toast).
  const requestDeleteProject = () => {
    const projectId = id!;
    setShowDeleteProject(false);
    undoableDeleteProject({
      qc: queryClient, id: projectId,
      onRestore: () => navigate(`/projects/${projectId}`, { replace: true }),
      onError: (err) => {
        toast('error', apiErrorMessage(err, t('project.deleteProjectError')));
        navigate(`/projects/${projectId}`, { replace: true });
      },
    });
    navigate('/projects');
  };

  // P-02a — "ביטול הפרויקט" (reason required), then archive / restore.
  const refreshProject = () => {
    queryClient.invalidateQueries({ queryKey: ['project', id] });
    queryClient.invalidateQueries({ queryKey: ['projects'] });
    queryClient.invalidateQueries({ queryKey: ['project-hours', id] });
    queryClient.invalidateQueries({ queryKey: ['project-team', id] });
  };
  const cancelProjectMutation = useMutation({
    mutationFn: (reason: string) => api.updateProject(id!, { status: 'cancelled', cancellation_reason: reason }),
    onSuccess: () => {
      refreshProject();
      setShowCancelProject(false);
      toast('success', t('project.projectCancelled'));
    },
  });
  // D-265 round 4 (QA-D265 r3 minor 1): "החזרה מהארכיון" of the engagement, on its project page (OTE manager).
  const restoreEngagementMutation = useMutation({
    mutationFn: (engagementId: string) => api.restoreEngagement(engagementId),
    onSuccess: () => { refreshProject(); queryClient.invalidateQueries({ queryKey: ['engagements'] }); toast('success', t('engagements.toast.restored')); },
    onError: (err) => toast('error', apiErrorMessage(err, t('engagements.errors.restore'))),
  });
  const archiveProjectMutation = useMutation({
    mutationFn: (action: 'archive' | 'restore') => (action === 'archive' ? api.archiveProject(id!) : api.restoreProject(id!)),
    onSuccess: (_res, action) => {
      refreshProject();
      // P-27 (D-166 s.11): an archive happens at once and "בטל" restores it through the server.
      if (action === 'archive') offerUndoStatus({ message: t('project.projectArchived'), revert: () => api.restoreProject(id!), onReverted: refreshProject });
      else toast('success', t('project.projectRestored'));
    },
    onError: (err) => toast('error', apiErrorMessage(err, t('project.actionFailed'))),
  });
  // P-04/C — "שינוי סטטוס"; A2 — "שינוי שלב" / "חזרה לחישוב אוטומטי".
  const statusMutation = useMutation({
    // D-303: ending a project / development carries its "אחריות ללקוח" (a date, or "אין אחריות").
    mutationFn: ({ status, warranty }: { status: string; warranty?: WarrantyPatch }) =>
      api.updateProject(id!, { status: status as any, ...(warranty ?? {}) }),
    onMutate: () => ({ prev: project?.status as string | undefined }),
    onSuccess: (_r, { status }, ctx) => {
      refreshProject(); setShowStatus(false);
      // P-27 (D-166 s.11): "בטל" sends the previous status through the same route. Not offered
      // for a transition into / out of completed or cancelled (completion events, cancel reason).
      if (statusIsReversible(ctx?.prev, status, PROJECT_ONE_WAY_STATUSES)) {
        offerUndoStatus({
          message: t('undoToast.statusChanged', { status: t(`status.${status}`, { defaultValue: status }) }),
          revert: () => api.updateProject(id!, { status: ctx!.prev as any }),
          onReverted: refreshProject,
        });
      } else toast('success', t('projectPage.status.saved'));
    },
  });
  const phaseMutation = useMutation({
    mutationFn: (phase: CorePhase | null) => api.updateProject(id!, { current_phase_manual: phase }),
    onSuccess: (_r, phase) => {
      refreshProject();
      setShowPhase(false);
      toast('success', t(phase ? 'projectPage.phase.saved' : 'projectPage.phase.backToAutoDone'));
    },
    onError: (err) => toast('error', apiErrorMessage(err, t('project.actionFailed'))),
  });

  if (isLoading) return <DetailSkeleton />;
  if (!project) {
    return (
      <DetailSection>
        {projectError ? (
          <StateBlock
            kind="error"
            size="page"
            error={projectError}
            title={t('project.loadError')}
            onRetry={() => refetch()}
            retrying={isFetching}
            action={<Link to="/projects" className="btn btn-secondary">{t('common.backToProjects')}</Link>}
          />
        ) : (
          <EmptyState
            icon={FolderKanban}
            title={t('project.notFound')}
            action={<Link to="/projects" className="btn btn-secondary">{t('common.backToProjects')}</Link>}
          />
        )}
      </DetailSection>
    );
  }

  const milestones = project.milestones || [];
  const tasksWithMilestones = (project as any).tasks || [];
  const tasks = tasksWithMilestones;
  const engagementId: string | null = (project as any).engagement_id ?? null;
  const metrics = project.metrics ?? null;
  const stats = (project as any).stats as { tickets_total: number; tickets_closed: number; total_actual_hours: number; completion_percentage: number } | undefined;
  // QA-R2/2 (D-53) — every action shows only with the server's permission
  // (`permissions`), never a guess from the role; none sent = none shown.
  const permissions = project.permissions ?? { can_edit: false, can_edit_commercial: false, can_view_financials: false };
  // "ביטול הפרויקט" / "העברה לארכיון" / "שחזור" / "מחיקת הפרויקט" — the delete permission (P-02a/C3).
  const canDeleteProject = permissions.can_delete === true;
  // P-05/G (D-53) — the milestone buttons show only with the permission (the API's `permissions`).
  // Q-DQ-173 (D-313): a cancelled project takes no new work (the server refuses too) - editing and restoring stay.
  const projectCancelled = (project as { status?: string }).status === 'cancelled';
  const canCreateMilestone = permissions.can_create_milestone === true && !projectCancelled;
  const canEditMilestone = permissions.can_edit_milestone === true;
  const canDeleteMilestone = permissions.can_delete_milestone === true;
  const canBulkAdd = permissions.can_bulk_add === true;
  const kind = (project.kind ?? project.type) as string;
  const isProjectKind = kind === 'project';
  // D-265 (QA-D265 #2): archived with its engagement - read only (the server refuses changes, 409).
  const engagementArchived = !!(project as { engagement_archived_at?: string | null }).engagement_archived_at;

  const breadcrumbItems = [
    orgCrumb(project.organization_id, project.organization_name || t('project.orgLabel'), internalOrgId, t('engagementChain.internalWork')),
    { label: project.name },
  ];

  const visibleTaskCount = tasks.length;
  // Canonical: the project's milestones (project → milestone → task), not a count per task.
  const milestoneTotal = milestones.length;

  // P-02a/C3 — the project's state decides its lifecycle action: not cancelled →
  // "ביטול הפרויקט"; cancelled → "העברה לארכיון"; archived → "שחזור מארכיון".
  // "מחיקת הפרויקט" is there (with the delete permission), enabled only for an empty project.
  const archived = !!project.archived_at;
  const cancelled = project.status === 'cancelled';
  const lifecycleItem: MenuItemDef = archived
    ? { key: 'restore', label: t('project.restoreProjectAction'), icon: <ArchiveRestore size={16} strokeWidth={1.75} />, separatorBefore: true, disabled: archiveProjectMutation.isPending, onSelect: () => archiveProjectMutation.mutate('restore') }
    : cancelled
      ? { key: 'archive', label: t('project.archiveProjectAction'), icon: <Archive size={16} strokeWidth={1.75} />, separatorBefore: true, disabled: archiveProjectMutation.isPending, onSelect: () => archiveProjectMutation.mutate('archive') }
      : { key: 'cancel', label: t('project.cancelProjectAction'), icon: <Ban size={16} strokeWidth={1.75} />, separatorBefore: true, onSelect: () => setShowCancelProject(true) };
  const deletable = project.deletable !== false;
  const manualPhase = metrics?.current_phase_manual ?? null;
  const moreItems: MenuItemDef[] = [
    ...(permissions.can_edit && !cancelled ? [{ key: 'status', label: t('projectPage.status.action'), icon: <ListChecks size={16} strokeWidth={1.75} />, onSelect: () => setShowStatus(true) }] : []),
    // A2 — the phase belongs to a project (not a development).
    ...(permissions.can_edit && isProjectKind && !cancelled ? [{ key: 'phase', label: t('projectPage.phase.action'), icon: <Milestone size={16} strokeWidth={1.75} />, onSelect: () => setShowPhase(true) }] : []),
    ...(permissions.can_edit && isProjectKind && manualPhase ? [{ key: 'phase-auto', label: t('projectPage.phase.backToAuto'), icon: <RotateCcw size={16} strokeWidth={1.75} />, disabled: phaseMutation.isPending, onSelect: () => phaseMutation.mutate(null) }] : []),
    // P-22 (D-156 #3) — the three buttons that used to sit under the header live in ⋯ (the four header buttons stay: D-39).
    ...(canBulkAdd ? [{ key: 'bulk-add', label: t('projectPage.bulkAdd'), icon: <Layers size={16} strokeWidth={1.75} />, onSelect: () => setShowBulkAdd(true) }] : []),
    ...(permissions.can_view_financials && project.financials ? [{ key: 'financials', label: t('projectPage.financials.button'), icon: <Wallet size={16} strokeWidth={1.75} />, onSelect: () => setShowFinancials(true) }] : []),
    { key: 'documents', label: t('projectPage.documents.button'), icon: <FileStack size={16} strokeWidth={1.75} />, onSelect: () => setShowDocuments(true) },
    ...(isAdmin ? [{ key: 'sla', label: t('project.slaMenu'), icon: <Shield size={16} strokeWidth={1.75} />, onSelect: () => setShowSla(true) }] : []),
    ...(canDeleteProject ? [
      lifecycleItem,
      {
        key: 'delete', label: t('project.deleteProjectAction'), icon: <Trash2 size={16} strokeWidth={1.75} />, danger: true,
        disabled: !deletable, hint: deletable ? undefined : t('project.deleteDisabledHasData'),
        onSelect: () => setShowDeleteProject(true),
      } as MenuItemDef,
    ] : []),
  ];
  // The first item opens no section (no separator above it).
  if (moreItems.length > 0 && moreItems[0].separatorBefore) moreItems[0] = { ...moreItems[0], separatorBefore: false };

  const openCreateTask = () => setShowCreateTask(true);

  // ---- the data lines (P-04/C) ----
  const statusLabel = getProjectStatusLabel(project.status, t);
  const statusText = metrics?.display_phase ? `${statusLabel} — ${t(`projectPhase.${metrics.display_phase}`)}` : statusLabel;
  const dash = <span className="text-surface-400">—</span>;
  const sep = <span className="text-surface-300" aria-hidden="true">·</span>;
  const plannedText = metrics && metrics.planned_hours !== null ? hoursText(metrics.planned_hours) : (metrics?.size_masked ? '—' : t('projectPage.notDefined'));
  const overdue = metrics?.overdue_days ?? null;

  return (
    <div className="max-w-7xl space-y-6">
      {/* PageChrome: the compact title and "משימה חדשה" move into the TopBar once the title row
          (h1 + actions, one row on desktop) scrolls away. */}
      <PageChrome
        title={project.name}
        action={projectCancelled ? undefined : { label: t('project.newTaskAction'), icon: <Plus size={16} aria-hidden="true" />, onClick: openCreateTask }}
      />
      <DetailHeader
        breadcrumb={breadcrumbItems}
        title={project.name}
        badges={
          <>
            {/* FQ-045 (D-225 s.3): the status pill opens the list of allowed statuses (same route as "שינוי סטטוס"; cancel stays its own action). */}
            <StatusPillSelect<string>
              value={project.status}
              options={(CHANGEABLE_STATUSES as readonly string[]).includes(project.status)
                ? CHANGEABLE_STATUSES.map((st) => ({
                  value: st as string,
                  label: st === project.status ? statusText : getProjectStatusLabel(st, t),
                  tone: statusTone(st),
                }))
                : [{ value: project.status, label: statusText, tone: statusTone(project.status) }]}
              onChange={(st) => {
                // D-303: ending it asks for the warranty first (a small window), nothing is sent before.
                if (needsWarrantyOnStatus(kind, project.status, st)) { setPickedStatus(st); setShowStatus(true); return; }
                statusMutation.mutate({ status: st });
              }}
              disabled={!permissions.can_edit || cancelled}
              pending={statusMutation.isPending}
              label={`${t('projectPage.status.action')}: ${statusText}`}
              sheetTitle={t('projectPage.status.title')}
            />
            {archived && <Badge>{t('project.archivedBadge')}</Badge>}
            {!isProjectKind && <Badge>{getProjectTypeLabel(project.type, t)}</Badge>}
          </>
        }
        actions={(
          <>
            {permissions.can_edit && (
              <button type="button" onClick={() => setShowEdit(true)} className="btn btn-secondary">
                <Pencil size={16} aria-hidden="true" /> {t('projectPage.edit')}
              </button>
            )}
            {canCreateMilestone && (
              <button type="button" onClick={() => setShowCreateMilestone(true)} className="btn btn-secondary">
                <Flag size={16} aria-hidden="true" /> {t('projectPage.addMilestone')}
              </button>
            )}
            {!engagementArchived && (
              projectCancelled ? null : (
                <button type="button" onClick={openCreateTask} className="btn btn-primary">
                  <Plus size={16} aria-hidden="true" /> {t('project.newTaskAction')}
                </button>
              )
            )}
            {!engagementArchived && moreItems.length > 0 && (
              <MenuButton
                items={moreItems}
                label={t('uxDetails.moreActions')}
                triggerClassName="btn btn-secondary px-2.5"
                showTooltip
              >
                <MoreHorizontal size={18} aria-hidden="true" />
              </MenuButton>
            )}
          </>
        )}
      >
        <CancellationNote source={project} />
        <div className="space-y-2 text-sm text-surface-700">
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span>{t('projectPage.customer')}: <OrgLink orgId={project.organization_id} name={project.organization_name} className="text-accent-text hover:underline underline-offset-2" /></span>
            {sep}
            <span>{t('projectPage.projectManager')}: {project.project_manager?.name ?? dash}</span>
            {sep}
            <span>{t('projectPage.teamLead')}: {project.team_lead?.name ?? dash}</span>
          </p>
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="tabular-nums">
              {formatDate(project.start_date)} <DirIcon name="forward" size={14} className="inline-block align-middle text-surface-400" aria-hidden /> {formatDate(project.end_date)}
              {overdue !== null && overdue > 0 && (
                <span className="ms-1 font-medium text-danger-700">({t('projectPage.lateBy', { count: overdue })})</span>
              )}
            </span>
            {sep}
            <span>{t('projectPage.hoursLine')}: <span className="tabular-nums">{plannedText} / {hoursText(metrics?.actual_hours ?? 0)}</span></span>
            {sep}
            <span data-testid="project-warranty-line">{t('warranty.label')}: {(() => {
              const w = warrantyOf(project);
              return w.none ? t('warranty.noneShown') : w.until ? t('warranty.until', { date: formatDate(w.until) }) : t('warranty.notSet');
            })()}</span>
            {metrics && (
              <>
                {sep}
                <span className="inline-flex items-center gap-1.5">{t('projectPage.health')}: <Badge tone={HEALTH_TONE[metrics.health]}>{t(`projectHealth.${metrics.health}`)}</Badge></span>
              </>
            )}
          </p>
          {metrics && (
            <div className="flex max-w-md items-center gap-3">
              <span className="text-xs text-surface-600">{t('projectPage.progress')}</span>
              <div className="h-2 flex-1 rounded-full bg-surface-100" role="progressbar" aria-valuenow={metrics.progress} aria-valuemin={0} aria-valuemax={100} aria-label={t('projectPage.progress')}>
                <div className="h-2 rounded-full bg-primary-600 transition-[width] duration-200" style={{ width: `${metrics.progress}%` }} />
              </div>
              <span className="text-sm font-semibold tabular-nums text-surface-900">{metrics.progress}%</span>
            </div>
          )}
        </div>
      </DetailHeader>
      {/* FIX-D337 (D-337 §6): "מסמכי האפיון" + the checklists for the delivery (a project opened from a package). */}
      <ProjectCharacterizationDocs engagementId={engagementId} />

      {engagementArchived && (
        <div role="status" data-testid="project-engagement-archived-banner" className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-warning-300 bg-warning-50 p-3 text-sm text-surface-900">
          <span className="flex items-center gap-2">
            <Archive size={16} aria-hidden="true" />
            {t('engagements.archivedBanner', { date: formatDate((project as { engagement_archived_at?: string | null }).engagement_archived_at as string) })}
          </span>
          {isAdmin && engagementId && (
            <button type="button" className="btn btn-secondary" disabled={restoreEngagementMutation.isPending} aria-busy={restoreEngagementMutation.isPending || undefined} onClick={() => restoreEngagementMutation.mutate(engagementId)}>
              <ArchiveRestore size={16} aria-hidden="true" /> {t('engagements.restoreFromArchive')}
            </button>
          )}
        </div>
      )}

      {project.description && <p className="max-w-3xl text-sm text-surface-600">{project.description}</p>}

      {/* P-28b (D-169) - the "negative profit" warning, at the top, for who sees money (none for a project with no price, T-46) */}
      <NegativeProfitNotice projectId={id!} enabled={canProfit} />

      {/* P-07 (D-74 §8) — opened from an "אפיון" card */}
      {project.characterization && <CharacterizationBanner card={project.characterization} />}

      {/* Deadline banner */}
      <DeadlineBanner
        dueDate={project.end_date}
        source="project"
        warningDays={project.deadline_warning_days || 7}
        entityLabel={t('project.theProject')}
      />

      {/* P-22 (D-156 #3) - Roni's four key numbers, back at the top (GET /projects/:id stats: every ticket and task of the project). */}
      {stats && (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="project-kpis">
          <Kpi label={t('project.kpiItems')} value={formatNumber(stats.tickets_total)} />
          <Kpi label={t('project.kpiItemsDone')} value={formatNumber(stats.tickets_closed)} />
          <Kpi label={t('project.actualHours')} value={formatNumber(metrics?.actual_hours ?? stats.total_actual_hours, 1)} />
          <Kpi label={t('project.kpiDonePct')} value={`${formatNumber(stats.completion_percentage)}%`} />
        </div>
      )}

      {/* Sections */}
      <Tabs
        ariaLabel={t('uxDetails.projectSections')}
        value={activeTab}
        onChange={setActiveTab}
        items={[
          { key: 'milestones', label: t('projects.milestonesTab'), count: milestoneTotal },
          { key: 'tasks', label: t('projects.tasksTab'), count: visibleTaskCount },
          { key: 'hours', label: t('projectPage.hoursTab') },
          { key: 'team', label: t('project.teamTab') },
          ...(canProfit ? [{ key: 'profitability' as const, label: t('profitability.tab') }] : []),
        ]}
      />

      <div role="tabpanel" aria-label={t(
        activeTab === 'tasks' ? 'projects.tasksTab' : activeTab === 'milestones' ? 'projects.milestonesTab' : activeTab === 'hours' ? 'projectPage.hoursTab' : activeTab === 'profitability' ? 'profitability.tab' : 'project.teamTab',
      )}>
        {activeTab === 'milestones' && (
          <ProjectMilestonesTab
            milestones={milestones as any}
            projectId={id!}
            canCreate={canCreateMilestone}
            canEdit={canEditMilestone}
            canDelete={canDeleteMilestone}
            onCreate={() => setShowCreateMilestone(true)}
            onCreateSub={(parent) => setSubParentId(parent.id)}
            onEdit={setEditingMilestone}
          />
        )}
        {activeTab === 'tasks' && (
          <TaskTable
            onFillEstimate={permissions.can_edit && !projectCancelled ? (tk: any) => setEditingTask(tk) : undefined}
            tasks={tasksWithMilestones}
            milestones={milestones as any[]}
            isAdmin={isAdmin}
            canDeleteTask={permissions.can_delete_task === true}
            organizationId={project.organization_id}
            projectId={id!}
            onCreateTask={() => setShowCreateTask(true)}
            onCreateTaskIn={(milestoneId) => setAddTasksTo({ id: milestoneId })}
            readOnly={engagementArchived}
            noNewWork={projectCancelled}
          />
        )}
        {activeTab === 'hours' && <ProjectHoursTab projectId={id!} />}
        {activeTab === 'team' && <ProjectTeamTab projectId={id!} canManage={permissions.can_edit} customerProject={project.organization_is_internal !== true} />}
        {activeTab === 'profitability' && <ProjectProfitabilityTab projectId={id!} canEdit={project?.permissions?.can_edit === true} />}
      </div>

      {/* Charts & Widgets (below work area) */}
      {(activeTab === 'tasks' || activeTab === 'milestones') && (
        <ChartsSection
          projectType={project.type}
          milestones={milestones}
        />
      )}

      {showEdit && (
        <ProjectFormModal
          mode="edit"
          kind={isProjectKind ? 'project' : 'development'}
          project={project as any}
          canEditCommercial={permissions.can_edit_commercial}
          showFinancials={permissions.can_view_financials}
          onClose={() => setShowEdit(false)}
          onSaved={() => { setShowEdit(false); refreshProject(); toast('success', t('projectForm.saved')); }}
        />
      )}

      {showFinancials && project.financials && (
        <ProjectFinancialsDialog
          kind={kind}
          financials={project.financials}
          canEdit={permissions.can_edit_commercial}
          onEdit={() => { setShowFinancials(false); setShowEdit(true); }}
          onClose={() => setShowFinancials(false)}
        />
      )}

      {showDocuments && (
        <ProjectDocumentsDialog
          projectId={id!}
          engagementId={engagementId}
          organizationId={project.organization_id}
          onClose={() => setShowDocuments(false)}
        />
      )}

      {showStatus && (
        <ProjectStatusDialog
          current={project.status}
          kind={kind}
          warranty={project}
          initialStatus={pickedStatus}
          pending={statusMutation.isPending}
          error={statusMutation.error}
          onConfirm={(s, w) => statusMutation.mutate({ status: s, warranty: w })}
          onClose={() => { setShowStatus(false); setPickedStatus(undefined); }}
        />
      )}

      {showPhase && (
        <ProjectPhaseDialog
          current={manualPhase}
          auto={metrics?.current_phase_auto ?? null}
          pending={phaseMutation.isPending}
          error={phaseMutation.error}
          onConfirm={(p) => phaseMutation.mutate(p)}
          onClose={() => setShowPhase(false)}
        />
      )}

      {showCreateMilestone && (
        <MilestoneFormModal
          variant="create"
          projectId={id!}
          organizationId={project.organization_id}
          customerName={project.organization_name}
          projectName={project.name}
          onClose={() => setShowCreateMilestone(false)}
          onDone={(ms, addTasks) => {
            queryClient.invalidateQueries({ queryKey: ['project', id] });
            queryClient.invalidateQueries({ queryKey: ['milestones', id] });
            setShowCreateMilestone(false);
            if (addTasks && ms?.id) setAddTasksTo({ id: ms.id });
          }}
        />
      )}

      {subParentId && (
        <MilestoneFormModal
          variant="create"
          projectId={id!}
          organizationId={project.organization_id}
          customerName={project.organization_name}
          projectName={project.name}
          parentId={subParentId}
          onClose={() => setSubParentId(null)}
          onDone={(ms, addTasks) => {
            queryClient.invalidateQueries({ queryKey: ['project', id] });
            queryClient.invalidateQueries({ queryKey: ['milestones', id] });
            setSubParentId(null);
            if (addTasks && ms?.id) setAddTasksTo({ id: ms.id });
          }}
        />
      )}

      {addTasksTo && (
        <TaskFormModal
          mode="create"
          placement={{ organizationId: project.organization_id, engagementId: engagementId ?? undefined, projectId: id!, milestoneId: addTasksTo.id }}
          lock="milestone"
          onClose={() => setAddTasksTo(null)}
          onCreated={() => {
            queryClient.invalidateQueries({ queryKey: ['project', id] });
            queryClient.invalidateQueries({ queryKey: ['project-tickets', id] });
            setAddTasksTo(null);
          }}
        />
      )}

      {showCreateTask && (
        <TaskFormModal
          mode="create"
          placement={{ organizationId: project.organization_id, engagementId: engagementId ?? undefined, projectId: id! }}
          lock="engagement"
          onClose={() => setShowCreateTask(false)}
          onCreated={() => {
            queryClient.invalidateQueries({ queryKey: ['project', id] });
            queryClient.invalidateQueries({ queryKey: ['project-tickets', id] });
            setShowCreateTask(false);
          }}
        />
      )}

      {editingTask && (
        <TaskFormModal
          mode="edit"
          variant="project"
          task={editingTask}
          organizationId={project.organization_id}
          onClose={() => setEditingTask(null)}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ['project', id] });
            queryClient.invalidateQueries({ queryKey: ['project-tickets', id] });
            setEditingTask(null);
          }}
        />
      )}

      {editingMilestone && (
        <MilestoneFormModal
          variant="edit"
          milestone={editingMilestone}
          organizationId={project.organization_id}
          customerName={project.organization_name}
          projectName={project.name}
          onClose={() => setEditingMilestone(null)}
          onDone={(ms, addTasks) => {
            queryClient.invalidateQueries({ queryKey: ['project', id] });
            queryClient.invalidateQueries({ queryKey: ['project-tickets', id] });
            queryClient.invalidateQueries({ queryKey: ['milestones', id] });
            queryClient.invalidateQueries({ queryKey: ['milestone', editingMilestone.id] });
            setEditingMilestone(null);
            if (addTasks) setAddTasksTo({ id: ms?.id ?? editingMilestone.id });
          }}
        />
      )}

      {showSla && <ProjectSlaDialog projectId={id!} project={project} onClose={() => setShowSla(false)} />}

      {showCancelProject && (
        <CancelProjectDialog
          pending={cancelProjectMutation.isPending}
          error={cancelProjectMutation.error}
          onConfirm={(reason) => cancelProjectMutation.mutate(reason)}
          onClose={() => setShowCancelProject(false)}
        />
      )}

      {/* Delete Project Confirmation */}
      <ConfirmDialog
        open={showDeleteProject}
        title={t('project.deleteProjectTitle')}
        description={t('project.deleteProjectConfirm', { name: project.name })}
        confirmLabel={t('project.deleteProjectAction')}
        onConfirm={requestDeleteProject}
        onCancel={() => setShowDeleteProject(false)}
        danger
      />

      {/* P-05/F — "הוספת אבני דרך ומשימות" (one transaction) */}
      {showBulkAdd && (
        <BulkAddModal
          projectId={id!}
          projectName={project.name}
          organizationId={project.organization_id}
          milestones={milestones as any}
          onClose={() => setShowBulkAdd(false)}
        />
      )}
    </div>
  );
}

// ============================================================
// The project's tasks (the "משימות" tab)
// ============================================================
const TASK_STATUSES = ['planning', 'in_progress', 'in_review', 'testing', 'completed'] as const;

// ============================================================
// TaskTable — the project's tasks (P-06/E2, D-62): grouped by milestone, each
// group with "+ הוספת משימה באבן הדרך הזו", and at the bottom "משימות ללא אבן
// דרך". Every row has the same ▶ and ⋯ (📎, logs, edit, cancel) as "כל המשימות".
// ============================================================
function TaskTable({ tasks, milestones, canDeleteTask, organizationId, projectId, onCreateTask, onCreateTaskIn, onFillEstimate, readOnly = false, noNewWork = false }: {
  /** D-328 - set only for those who may edit the project (team lead / PM): the click on "בלי הערכה". */
  onFillEstimate?: (task: any) => void;
  tasks: any[];
  milestones: any[];
  isAdmin: boolean;
  /** R-03 (D-94) - the server's `permissions.can_delete_task`. */
  canDeleteTask: boolean;
  organizationId: string;
  projectId: string;
  onCreateTask: () => void;
  onCreateTaskIn: (milestoneId: string) => void;
  /** D-265 round 4: archived with its engagement - no ▶, no status pill, no row actions, no "+". */
  readOnly?: boolean;
  /** Q-DQ-173: a cancelled project - no "new task" buttons. */
  noNewWork?: boolean;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { canPath } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const isDesktop = useIsDesktop();

  // G — the tab's filters live in the URL (back restores them).
  const filterWorker = searchParams.get('worker') ?? '';
  const filterStatus = searchParams.get('status') ?? '';
  const setParam = (key: string, value: string) => setSearchParams((prev) => {
    const next = new URLSearchParams(prev);
    if (value) next.set(key, value); else next.delete(key);
    return next;
  }, { replace: true });
  const setFilterWorker = (v: string) => setParam('worker', v);
  const setFilterStatus = (v: string) => setParam('status', v);
  const [deletingTask, setDeletingTask] = useState<{ id: string; title: string } | null>(null);
  const [loggingTask, setLoggingTask] = useState<{ id: string; title: string } | null>(null);
  // P-02a/C1 — the refused delete of a task with hours: offer to cancel it.
  const [taskWithWork, setTaskWithWork] = useState<{ id: string; minutes: number } | null>(null);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    queryClient.invalidateQueries({ queryKey: ['project-tickets', projectId] });
    queryClient.invalidateQueries({ queryKey: ['all-tasks'] });
  };
  const rowKit = useTaskRowActions({
    startInColumn: true,
    onChanged: refresh,
    extra: (tk) => [
      { key: 'log', label: t('tasks.list.logTime'), icon: <Clock size={16} strokeWidth={1.75} />, onSelect: () => setLoggingTask({ id: tk.id, title: tk.title }) },
      ...(canDeleteTask ? [{ key: 'delete', label: t('tasks.list.delete'), icon: <Trash2 size={16} strokeWidth={1.75} />, danger: true, separatorBefore: true, onSelect: () => setDeletingTask({ id: tk.id, title: tk.title }) }] : []),
    ],
  });

  const { data: agentsRes } = useQuery({
    queryKey: ['assignable-agents', organizationId],
    queryFn: () => api.getAssignableAgents(organizationId),
    staleTime: 60_000,
    enabled: !!organizationId,
  });
  const allAgents = useMemo(() => agentsRes?.data ?? [], [agentsRes]);
  const workers = useMemo(() => allAgents.map((a: any) => ({ id: a.id, name: a.display_name || a.name || a.email || a.id })), [allAgents]);

  const taskMatchesWorker = (tk: any, workerId: string) => {
    const agents = tk.agents as any[] | undefined;
    if (agents && agents.length > 0) return agents.some((a: any) => a.id === workerId);
    return tk.assigned_agent_id === workerId;
  };

  const visibleTasks = useWithoutHidden(DELETE_KINDS.ticket, tasks as { id: string }[]) as any[];
  const filtered = useMemo(() => visibleTasks.filter((tk: any) => {
    if (filterWorker && !taskMatchesWorker(tk, filterWorker)) return false;
    if (filterStatus && tk.status !== filterStatus) return false;
    return true;
  }), [visibleTasks, filterWorker, filterStatus]);

  const hasFilters = filterWorker !== '' || filterStatus !== '';
  const resetFilters = () => setSearchParams((prev) => { const n = new URLSearchParams(prev); n.delete('worker'); n.delete('status'); return n; }, { replace: true });

  // P-27 (D-166 s.11): hidden at once, DELETE after 6 s, "בטל" cancels it.
  const requestDeleteTask = (task: { id: string; title: string }) => {
    setDeletingTask(null);
    undoableDeleteTicket({
      qc: queryClient, id: task.id, taskType: 'task', onCommitted: refresh, onRestore: refresh,
      onError: (err) => {
        const minutes = taskHasWorkMinutes(err);
        if (minutes !== null) setTaskWithWork({ id: task.id, minutes });
        else toast('error', apiErrorMessage(err, t('project.deleteTaskError')));
      },
    });
  };

  const filterDefs: FilterDef[] = [
    { key: 'status', label: t('project.filterByStatus'), value: filterStatus, allLabel: t('project.allStatuses'), options: TASK_STATUSES.map((s) => ({ value: s, label: t(`status.${s}`) })), onChange: setFilterStatus },
    { key: 'worker', kind: 'search-select', label: t('project.filterByWorker'), value: filterWorker, allLabel: t('project.allWorkers'), options: workers.map((w) => ({ value: w.id, label: w.name })), onChange: setFilterWorker },
  ];

  const columns: Column<any>[] = [
    ...(readOnly ? [] : [rowKit.startColumn() as Column<any>]),
    ...taskListColumns<any>(t, readOnly ? { hide: ['customer', 'type', 'container', 'milestone'] } : { hide: ['customer', 'type', 'container', 'milestone'], statusCell: (tk) => rowKit.statusCell(tk), ...(onFillEstimate ? { onFillEstimate } : {}) }),
  ];
  const card = (tk: any): CardSpec => ({
    title: <bdi>{tk.title}</bdi>,
    end: tk.due_date ? <DueWithLate task={tk} t={t} /> : undefined,
    badges: <StatusBadge status={tk.status as any} />,
    metaItems: [assigneeNames(tk).join(', ')].filter(Boolean),
  });

  // The groups: the project's milestones in their order (sub-milestones under
  // their parent), then the tasks with no milestone.
  const ordered = milestoneTree(milestones);
  const byMilestone = new Map<string, any[]>();
  const direct: any[] = [];
  for (const tk of filtered) {
    const msId = (tk.milestones || [])[0]?.id ?? tk.milestone_id ?? null;
    if (msId) byMilestone.set(msId, [...(byMilestone.get(msId) ?? []), tk]);
    else direct.push(tk);
  }
  const groups = projectTaskGroups(ordered, (msId) => byMilestone.get(msId)?.length ?? 0, hasFilters);

  // F3b-1 — like TaskRowsTable (QA-F3): from md up DataTable's own sideways
  // scroll (mobile="scroll"), so at 1366 the task name keeps a readable width
  // (min-w-40), stays sticky and is not cut; phones keep the cards.
  const table = (rows: any[], caption: string) => (
    <DataTable
      columns={columns}
      data={rows}
      keyField="id"
      caption={caption}
      mobile={isDesktop ? 'scroll' : 'cards'}
      getRowHref={(tk) => taskPath(tk.id)}
      rowLabel={(tk) => tk.title}
      card={card}
      rowActions={readOnly ? undefined : (tk) => rowKit.rowActions(tk)}
      emptyState={<p className="px-3 py-4 text-sm text-text-2">{t('projectTasks.noTasksInGroup')}</p>}
    />
  );

  return (
    <section aria-labelledby="project-tasks-title" className="space-y-3">
      <h2 id="project-tasks-title" className="sr-only">{t('projects.tasksTab')}</h2>
      {tasks.length > 0 && (
        <FilterBar
          filters={filterDefs}
          inlineFilters={0}
          onClearFilters={resetFilters}
          onClearAll={resetFilters}
          summary={hasFilters ? t('project.tasksShown', { shown: filtered.length, count: tasks.length }) : undefined}
        />
      )}

      {tasks.length === 0 && milestones.length === 0 ? (
        <EmptyState
          icon={ListTodo}
          title={t('project.noTasks')}
          action={readOnly || noNewWork ? undefined : <button type="button" onClick={onCreateTask} className="btn btn-secondary"><Plus size={16} aria-hidden="true" /> {t('project.newTaskAction')}</button>}
        />
      ) : hasFilters && filtered.length === 0 ? (
        <EmptyState
          icon={SearchX}
          title={t('project.noTasksMatch')}
          action={<button type="button" className="btn btn-secondary" onClick={resetFilters}>{t('project.clearFiltersAction')}</button>}
        />
      ) : (
        <>
          {groups.map(({ m, depth, archived: groupArchived }) => {
            const rows = byMilestone.get(m.id) ?? [];
            return (
              <section key={m.id} aria-labelledby={`ms-group-${m.id}`} className={`rounded-xl border border-border bg-surface ${depth > 0 ? 'ms-6' : ''}`} data-testid="project-task-group">
                <div className="flex flex-wrap items-center justify-between gap-2 px-4 pt-3">
                  <h3 id={`ms-group-${m.id}`} className="text-sm font-semibold text-text">
                    <Link to={`/milestones/${m.id}`} className="hover:underline">
                      {/* QA-R2/1 (P-02a/C2) — an archived milestone's group is marked. */}
                      <bdi>{m.name}</bdi>{groupArchived && <> {t('projectTasks.archivedMark')}</>}
                    </Link>
                    <span className="ms-2 text-xs font-normal text-text-2 tabular-nums">{t('projectTasks.count', { count: rows.length })}</span>
                  </h3>
                  {!m.archived_at && !readOnly && !noNewWork && (
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => onCreateTaskIn(m.id)}>
                      <Plus size={14} aria-hidden="true" /> {t('projectTasks.addInMilestone')}
                    </button>
                  )}
                </div>
                <div className="p-2">{table(rows, t('projectTasks.groupCaption', { name: m.name }))}</div>
              </section>
            );
          })}
          <section aria-labelledby="ms-group-none" className="rounded-xl border border-border bg-surface" data-testid="project-task-group-direct">
            <div className="flex flex-wrap items-center justify-between gap-2 px-4 pt-3">
              <h3 id="ms-group-none" className="text-sm font-semibold text-text">
                {t('projectTasks.withoutMilestone')}
                <span className="ms-2 text-xs font-normal text-text-2 tabular-nums">{t('projectTasks.count', { count: direct.length })}</span>
              </h3>
              {!readOnly && !noNewWork && (
                <button type="button" className="btn btn-ghost btn-sm" onClick={onCreateTask}>
                  <Plus size={14} aria-hidden="true" /> {t('project.newTaskAction')}
                </button>
              )}
            </div>
            <div className="p-2">{table(direct, t('projectTasks.withoutMilestone'))}</div>
          </section>
        </>
      )}
      {rowKit.dialogs}
      {loggingTask && (
        <NewWorkLogModal
          initialTab="tasks"
          initialItem={{ id: loggingTask.id, title: loggingTask.title } as Ticket}
          onClose={() => setLoggingTask(null)}
          onSuccess={() => {
            setLoggingTask(null);
            toast('success', t('quickCreate.workLogCreated'), canPath('/work-logs') ? {
              action: { label: t('quickCreate.viewWorkLogs'), onClick: () => navigate('/work-logs') },
            } : undefined);
          }}
        />
      )}

      <ConfirmDialog
        open={!!deletingTask}
        title={t('project.deleteTaskTitle')}
        description={deletingTask ? t('project.deleteTaskConfirm', { name: deletingTask.title }) : undefined}
        confirmLabel={t('project.deleteTaskAction')}
        onConfirm={() => { if (deletingTask) requestDeleteTask(deletingTask); }}
        onCancel={() => setDeletingTask(null)}
        danger
      />

      {taskWithWork && (
        <TaskHasWorkDialog
          minutes={taskWithWork.minutes}
          onCancelTask={() => navigate(`${taskPath(taskWithWork.id)}?action=cancel`)}
          onClose={() => setTaskWithWork(null)}
        />
      )}
    </section>
  );
}

// ============================================================
// CancelProjectDialog — "ביטול הפרויקט" with a required reason (P-02a/A1)
// ============================================================
function CancelProjectDialog({ pending, error, onConfirm, onClose }: {
  pending: boolean;
  error: unknown;
  onConfirm: (reason: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [fieldError, setFieldError] = useState<string | undefined>();
  const confirmOnce = useConfirmLock(pending); // DQ-175: lock at the click
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) { setFieldError(t('project.cancellationReasonRequired')); return; }
    confirmOnce(() => onConfirm(reason.trim()));
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={t('project.cancelProjectAction')}
      description={t('project.cancelProjectHelp')}
      size="md"
      dismissible={!pending}
      footer={(
        <>
          <button type="button" onClick={onClose} className="btn btn-secondary" disabled={pending}>{t('common.back')}</button>
          <SubmitButton form="cancel-project-form" busy={pending}>{t('project.cancelProjectAction')}</SubmitButton>
        </>
      )}
    >
      <form id="cancel-project-form" onSubmit={submit} noValidate className="space-y-3">
        <ServerError error={error} fallback={t('project.cancelProjectFailed')} />
        <Field label={t('project.cancellationReason')} id="cancel-project-reason" required error={fieldError}>
          <textarea
            className="input min-h-20"
            value={reason}
            onChange={(e) => { setReason(e.target.value); if (fieldError && e.target.value.trim()) setFieldError(undefined); }}
          />
        </Field>
      </form>
    </Dialog>
  );
}

// ============================================================
// Charts Section — diagrams & widgets below the work area
// ============================================================
function ChartsSection({
  projectType, milestones,
}: {
  projectType: string;
  milestones: any[];
}) {
  // P-02b — a project page is a project or a development; only a development has charts.
  if (projectType === 'development') {
    return <DevelopmentCharts milestones={milestones} />;
  }

  return null;
}

function DevelopmentCharts({ milestones }: { milestones: any[] }) {
  return (
    <MilestoneProgressList
      milestones={milestones.map((ms) => ({
          id: ms.id,
          name: ms.name,
          status: ms.status,
          dueDate: ms.due_date,
          ticketsTotal: ms.tickets_total || 0,
          ticketsClosed: ms.tickets_closed || 0,
          estimatedHours: parseFloat(ms.estimated_hours) || 0,
        actualHours: parseFloat(ms.total_actual_hours) || 0,
      }))}
    />
  );
}

// ============================================================
// Project SLA override (⋯ "הגדרת SLA לפרויקט", admin)
// ============================================================
function ProjectSlaDialog({ projectId, project, onClose }: { projectId: string; project: any; onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [responseHours, setResponseHours] = useState(project.sla_response_hours ?? '');
  const [resolutionHours, setResolutionHours] = useState(project.sla_resolution_hours ?? '');
  // FIX-B38 (QX-841): the fields are filled once, when the dialog opens (it mounts on open).
  // A live refetch of the project (another user's change) must not overwrite what is being typed.

  // Same request as the old inline editor: an empty field clears the override (inherit from the customer).
  const mutation = useMutation({
    mutationFn: () =>
      api.updateProject(projectId, {
        sla_response_hours: responseHours === '' ? null : parseFloat(String(responseHours)),
        sla_resolution_hours: resolutionHours === '' ? null : parseFloat(String(resolutionHours)),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      toast('success', t('project.slaSaved'));
      onClose();
    },
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('project.slaMenu')}
      description={t('project.slaHint')}
      size="md"
      dismissible={!mutation.isPending}
      footer={(
        <>
          <button type="button" onClick={onClose} className="btn btn-secondary" disabled={mutation.isPending}>{t('common.cancel')}</button>
          <SubmitButton form="project-sla-form" busy={mutation.isPending}>{t('project.slaSave')}</SubmitButton>
        </>
      )}
    >
      <form id="project-sla-form" onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }} className="space-y-4">
        <ServerError error={mutation.error} fallback={t('project.slaSaveFailed')} />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label={t('sla.responseTime')} id="project-sla-response" hint={t('project.slaEmptyHint')}>
            <input type="number" inputMode="decimal" step="0.5" min="0" className="input" value={responseHours} onChange={(e) => setResponseHours(e.target.value)} />
          </Field>
          <Field label={t('sla.resolutionTime')} id="project-sla-resolution" hint={t('project.slaEmptyHint')}>
            <input type="number" inputMode="decimal" step="1" min="0" className="input" value={resolutionHours} onChange={(e) => setResolutionHours(e.target.value)} />
          </Field>
        </div>
      </form>
    </Dialog>
  );
}
